import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  RefreshCw,
  BarChart3,
  TrendingUp,
  PieChart,
  Target,
  Briefcase,
  Shield,
  Download,
  Upload
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ["#2a9d8f", "#e9c46a", "#f4a261", "#e76f51", "#40c9ba", "#264653", "#52b788"];

const STATUS_COLORS = {
  new: "#2a9d8f",
  contacted: "#e9c46a",
  call_set: "#f4a261",
  submitted: "#40c9ba",
  interview: "#52b788",
  offer: "#95d5b2",
  rejected: "#e76f51",
  archived: "#6c757d",
};

export default function Analytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await api.get('/analytics');
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Failed to load analytics");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const handleExportCSV = () => {
    window.open(`${API_URL}/api/leads/export/csv`, '_blank');
    toast.success("Downloading CSV...");
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/leads/import/csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const result = response.data;
      toast.success(`Imported ${result.imported} leads (${result.skipped} skipped)`);
      if (result.errors.length > 0) {
        console.error("Import errors:", result.errors);
      }
      fetchAnalytics();
    } catch (error) {
      toast.error("Failed to import CSV");
      console.error(error);
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-theme-darker border-2 border-theme-primary/50 p-3 rounded-lg shadow-lg">
          <p className="text-theme-secondary font-mono text-sm">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-theme-light text-sm">
              {entry.name}: <span className="font-bold">{entry.value}</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="analytics-loading">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-theme-primary mx-auto mb-4" />
          <p className="text-theme-secondary font-mono">LOADING ANALYTICS...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-12 rounded-xl bg-gradient-to-br from-[#e9c46a] to-[#d4a84b] flex items-center justify-center" style={{boxShadow: '4px 4px 0 rgba(139, 69, 19, 0.4)'}}>
            <BarChart3 className="w-7 h-7 text-theme-dark" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-theme-light" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.3)'}}>
              Analytics
            </h1>
            <p className="text-theme-muted text-sm font-mono">
              PIPELINE INSIGHTS & DATA MANAGEMENT
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <input
            type="file"
            accept=".csv"
            onChange={handleImportCSV}
            className="hidden"
            id="csv-import"
          />
          <Button 
            variant="outline"
            className="border-2 border-theme-primary/50 text-theme-primary-light hover:bg-theme-primary/20"
            onClick={() => document.getElementById('csv-import').click()}
            disabled={importing}
            data-testid="import-csv-btn"
          >
            {importing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            IMPORT CSV
          </Button>
          <Button 
            variant="outline"
            className="border-2 border-theme-secondary/50 text-theme-secondary hover:bg-theme-secondary/20"
            onClick={handleExportCSV}
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            EXPORT CSV
          </Button>
          <Button 
            className="bg-gradient-to-r from-[#2a9d8f] to-[#1a6b62] hover:from-[#40c9ba] hover:to-[#2a9d8f] text-theme-light border-2 border-[#40c9ba]/50"
            onClick={fetchAnalytics}
            data-testid="refresh-analytics-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            REFRESH
          </Button>
        </div>
      </div>

      {/* Conversion Rates */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { label: "New → Contacted", value: analytics?.conversion_rates?.new_to_contacted || 0, icon: TrendingUp },
          { label: "Contacted → Interview", value: analytics?.conversion_rates?.contacted_to_interview || 0, icon: Target },
          { label: "Interview → Offer", value: analytics?.conversion_rates?.interview_to_offer || 0, icon: Briefcase },
        ].map((metric, i) => (
          <div 
            key={i}
            className="p-5 rounded-xl"
            style={{
              background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
              border: '3px solid rgba(42, 157, 143, 0.3)',
              boxShadow: '4px 4px 0 rgba(38, 70, 83, 0.4)'
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#2a9d8f] to-[#1a6b62] flex items-center justify-center">
                <metric.icon className="w-6 h-6 text-theme-secondary" />
              </div>
              <div>
                <p className="text-theme-muted text-xs font-mono">{metric.label}</p>
                <p className="text-2xl font-bold text-theme-secondary" style={{textShadow: '0 0 10px rgba(233, 196, 106, 0.3)'}}>
                  {metric.value}%
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline Distribution */}
        <div className="rounded-xl overflow-hidden" style={{
          background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
          border: '3px solid rgba(233, 196, 106, 0.3)',
          boxShadow: '6px 6px 0 rgba(139, 69, 19, 0.2)'
        }}>
          <div className="p-5 border-b-2 border-theme-secondary/30 bg-theme-darker/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#e9c46a] to-[#d4a84b] flex items-center justify-center">
                <PieChart className="w-5 h-5 text-theme-dark" />
              </div>
              <div>
                <h2 className="text-theme-light font-bold">Pipeline Status</h2>
                <p className="text-xs text-theme-muted font-mono">LEAD DISTRIBUTION BY STAGE</p>
              </div>
            </div>
          </div>
          <div className="p-5 h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPie>
                <Pie
                  data={analytics?.pipeline_trends || []}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="count"
                  nameKey="status"
                  label={({ status, count }) => `${status}: ${count}`}
                  labelLine={false}
                >
                  {(analytics?.pipeline_trends || []).map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={STATUS_COLORS[entry.status] || COLORS[index % COLORS.length]}
                      stroke="rgba(38, 70, 83, 0.8)"
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </RechartsPie>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Score Distribution */}
        <div className="rounded-xl overflow-hidden" style={{
          background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
          border: '3px solid rgba(42, 157, 143, 0.3)',
          boxShadow: '6px 6px 0 rgba(38, 70, 83, 0.3)'
        }}>
          <div className="p-5 border-b-2 border-theme-primary/30 bg-theme-darker/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#2a9d8f] to-[#1a6b62] flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-theme-secondary" />
              </div>
              <div>
                <h2 className="text-theme-light font-bold">Score Distribution</h2>
                <p className="text-xs text-theme-muted font-mono">LEADS BY SCORE RANGE</p>
              </div>
            </div>
          </div>
          <div className="p-5 h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics?.score_distribution || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(42, 157, 143, 0.2)" />
                <XAxis dataKey="range" tick={{ fill: '#a8c8c4', fontSize: 12 }} />
                <YAxis tick={{ fill: '#a8c8c4', fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" fill="#2a9d8f" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Source Performance */}
        <div className="rounded-xl overflow-hidden" style={{
          background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
          border: '3px solid rgba(244, 162, 97, 0.3)',
          boxShadow: '6px 6px 0 rgba(139, 69, 19, 0.2)'
        }}>
          <div className="p-5 border-b-2 border-theme-accent/30 bg-theme-darker/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#f4a261] to-[#e76f51] flex items-center justify-center">
                <Target className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-theme-light font-bold">Source Performance</h2>
                <p className="text-xs text-theme-muted font-mono">LEADS & AVG SCORE BY SOURCE</p>
              </div>
            </div>
          </div>
          <div className="p-5 h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics?.source_distribution || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(42, 157, 143, 0.2)" />
                <XAxis type="number" tick={{ fill: '#a8c8c4', fontSize: 12 }} />
                <YAxis dataKey="source" type="category" tick={{ fill: '#a8c8c4', fontSize: 11 }} width={80} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="count" name="Leads" fill="#e9c46a" radius={[0, 4, 4, 0]} />
                <Bar dataKey="avg_score" name="Avg Score" fill="#2a9d8f" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Tech Stack */}
        <div className="rounded-xl overflow-hidden" style={{
          background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
          border: '3px solid rgba(64, 201, 186, 0.3)',
          boxShadow: '6px 6px 0 rgba(38, 70, 83, 0.3)'
        }}>
          <div className="p-5 border-b-2 border-[#40c9ba]/30 bg-theme-darker/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#40c9ba] to-[#2a9d8f] flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-theme-dark" />
              </div>
              <div>
                <h2 className="text-theme-light font-bold">Top Technologies</h2>
                <p className="text-xs text-theme-muted font-mono">MOST REQUESTED TECH STACK</p>
              </div>
            </div>
          </div>
          <div className="p-5 h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics?.top_tech_stack || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(42, 157, 143, 0.2)" />
                <XAxis type="number" tick={{ fill: '#a8c8c4', fontSize: 12 }} />
                <YAxis dataKey="tech" type="category" tick={{ fill: '#a8c8c4', fontSize: 11 }} width={100} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" fill="#40c9ba" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Clearance Breakdown */}
      {Object.keys(analytics?.clearance_breakdown || {}).length > 0 && (
        <div className="rounded-xl overflow-hidden" style={{
          background: 'linear-gradient(145deg, rgba(26, 58, 66, 0.9), rgba(38, 70, 83, 0.8))',
          border: '3px solid rgba(231, 111, 81, 0.3)',
          boxShadow: '6px 6px 0 rgba(139, 69, 19, 0.2)'
        }}>
          <div className="p-5 border-b-2 border-theme-danger/30 bg-theme-darker/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#e76f51] to-[#d45d3e] flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-theme-light font-bold">Clearance Requirements</h2>
                <p className="text-xs text-theme-muted font-mono">LEADS BY SECURITY CLEARANCE</p>
              </div>
            </div>
          </div>
          <div className="p-5">
            <div className="flex flex-wrap gap-3">
              {Object.entries(analytics?.clearance_breakdown || {}).map(([clearance, count]) => (
                <Badge 
                  key={clearance}
                  className="px-4 py-2 text-sm font-mono"
                  style={{
                    background: 'rgba(231, 111, 81, 0.2)',
                    color: '#f4a261',
                    border: '2px solid rgba(231, 111, 81, 0.4)'
                  }}
                >
                  {clearance}: {count}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
